import React, { useMemo, useState } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@node-space/hooks'
import type { ContactPaymentInstrument, MonolithWallet } from '@node-space/types'
import {
  Box,
  Button,
  Input,
  NoData,
  NoResults,
  RequestError,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useContactInstrumentsSelectorQuery } from 'hooks/queries/useContactInstrumentsSelectorQuery'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import {
  contactActionAtom,
  contactAtom,
  contactInstrumentActionAtom,
} from 'pages/Contacts/atoms/contactAtoms'
import { ContactsSheets } from 'pages/Contacts/components/ContactsSheets'
import { ContactInstrumentsSheet } from 'pages/Contacts/ContactInstruments'
import { stripSpecialCharacters } from 'pages/Contacts/utils/utils'
import { ContactActionIds, ContactInstrumentActionIds } from 'types/atoms/atom.contacts'
import { Contact, InstrumentType } from 'types/contacts'
import { ContactInstrumentsList } from './ContactInstrumentsList'

interface ContactSelectorProps {
  instrumentType?: InstrumentType
  wallet?: MonolithWallet
  onSelect: (contactPayment: ContactPaymentInstrument) => void
  onClose?: () => void
}

export const ContactSelector = ({
  instrumentType = InstrumentType.CRYPTO,
  wallet,
  onSelect,
  onClose,
}: ContactSelectorProps) => {
  const { t } = useTranslation()
  const { enableAddAccountSheet, enableCryptoFiatSend } = useFeatureFlags()
  const [searchQuery, setSearchQuery] = useState('')
  const [hasLoadedMore, setHasLoadedMore] = useState(false)
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false)
  const [isInstrumentSheetOpen, setIsInstrumentSheetOpen] = useState(false)
  const debouncedSearchTerm = useDebounce(searchQuery, 300)

  const setContact = useSetAtom(contactAtom)
  const setContactAction = useSetAtom(contactActionAtom)
  const setContactInstrumentAction = useSetAtom(contactInstrumentActionAtom)

  const {
    data: contactInstruments,
    fetchNextPage,
    hasNextPage,
    isError,
    isPending,
    isFetchingNextPage,
  } = useContactInstrumentsSelectorQuery(
    {
      searchTerm: debouncedSearchTerm,
      paymentMethod: instrumentType,
      walletLsid: wallet?.lsid,
      size: 100,
    },
    enableCryptoFiatSend
  )

  const hasContacts = !!contactInstruments?.length
  const noResults = !!debouncedSearchTerm && !hasContacts
  const showErrorState = isError && !isPending
  const showEmptyState = !isError && !isPending && !hasContacts && !debouncedSearchTerm
  const showSearch = hasContacts || noResults
  const showNoSearchResults = noResults && !isPending

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(stripSpecialCharacters(event.target.value))
  }

  const handleClearSearch = () => {
    setSearchQuery('')
  }

  const handleContactSelect = (contactPayment: ContactPaymentInstrument) => {
    if (contactPayment) {
      onSelect(contactPayment)
    }
  }

  const handleOpenContactSheet = () => {
    setContactAction(prev => ({
      ...prev,
      actionId: ContactActionIds.CONTACT,
      header: t('contacts.addContact'),
      onClose: () => {
        setIsContactSheetOpen(false)
        setContact({} as Contact)
      },
      meta: {
        enableCreateInstrument: true,
        flowType: enableAddAccountSheet ? undefined : instrumentType,
      },
    }))
    setIsContactSheetOpen(true)
  }

  const handleOpenInstrumentSheet = () => {
    setContact({} as Contact)
    setContactInstrumentAction(prev => ({
      ...prev,
      actionId: ContactInstrumentActionIds.CONTACT_INSTRUMENT_DECISION,
      onClose: () => {
        setIsInstrumentSheetOpen(false)
        setContact({} as Contact)
      },
    }))
    setIsInstrumentSheetOpen(true)
  }

  const showInlineAddContact = useMemo(() => {
    if (enableCryptoFiatSend && !!onClose) {
      return false
    }
    return true
  }, [enableCryptoFiatSend, onClose])

  return (
    <>
      {showErrorState && (
        <RequestError
          description={
            <>
              {`${t('requestErrors.comeBackLater')} `}
              <Button variant="ghost" noPadding onClick={() => globalThis.location.reload()}>
                {t('requestErrors.refreshPage')}
              </Button>
            </>
          }
        />
      )}

      {!isError && (
        <>
          {showSearch && (
            <Box paddingB={16} width="full" justify="between" flex gap={12}>
              <Box background="standard" width="full">
                <Input
                  value={searchQuery}
                  placeholderText={t(
                    enableAddAccountSheet
                      ? 'contacts.searchPlaceholderV2'
                      : 'contacts.searchPlaceholder'
                  )}
                  onChange={handleSearchChange}
                  searchable
                  testId="contact-search-field"
                />
              </Box>
              {showInlineAddContact && (
                <Button
                  testId="add-contact"
                  variant="secondary"
                  onClick={handleOpenInstrumentSheet}
                >
                  {t('contacts.addNew')}
                </Button>
              )}
            </Box>
          )}

          {isPending && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

          {showNoSearchResults && <NoResults onClear={handleClearSearch} />}

          {hasContacts && (
            <Box className="w-full">
              <ContactInstrumentsList
                contacts={contactInstruments}
                isFetchingNextPage={isFetchingNextPage}
                hasLoadedMore={hasLoadedMore}
                hasNextPage={hasNextPage ?? false}
                handleContactSelect={handleContactSelect}
                setHasLoadedMore={setHasLoadedMore}
                fetchNextPage={fetchNextPage}
              />
              {!showInlineAddContact && (
                <Box flex justify="between" paddingT={32}>
                  <Button
                    testId="cancel"
                    type="button"
                    variant="secondary"
                    onClick={() => onClose?.()}
                  >
                    {t('cancel')}
                  </Button>
                  <Button variant="primary" onClick={handleOpenInstrumentSheet}>
                    {t('contacts.orAddNew')}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      {showEmptyState && (
        <NoData
          dataName="contacts"
          description={t('contacts.addContactDescription')}
          PrimaryButton={
            <Button testId="add-contact" onClick={handleOpenContactSheet}>
              {t('contacts.addContact')}
            </Button>
          }
        />
      )}

      <ContactsSheets isSheetOpen={isContactSheetOpen} isNestedSheet />
      <ContactInstrumentsSheet isSheetOpen={isInstrumentSheetOpen} isNestedSheet />
    </>
  )
}
